/**
 * AddRecipientModal — operator UI for adding a person OR an entity-level
 * contact channel to a notification draft. See plan at
 * docs/wip/assayer-stream/NOTIFICATION_CONTACT_ENRICHMENT_PLAN.md
 *
 * Two tabs:
 *   • Person — name + role + organisation + email. Goes through Cynapse
 *     contact ingest (5-tier match). When Cynapse returns a fuzzy
 *     candidate (similarity 0.85-0.92) the modal swaps to a candidate-
 *     review panel with "Use existing" and "Create new anyway" buttons.
 *   • Entity channel — contact_type + email + phone. Lands in the
 *     Assayer entity_contacts table (precursor to Foundation Rework
 *     mig 149). No Cynapse round-trip.
 */

import { useState } from 'react';
import { X, AlertTriangle, CheckCircle2, UserPlus, AtSign } from 'lucide-react';
import {
  addRecipient,
  type AddPersonRecipientRequest,
  type AddEntityChannelRequest,
  type AddRecipientResult,
  type FuzzyCandidate,
  type PersonRoleEnum,
  type EntityChannelTypeEnum,
} from '../lib/api/notifications.js';
import { Modal, ModalHeader, ModalBody } from './ui/modal.js';

interface AddRecipientModalProps {
  isOpen: boolean;
  onClose: () => void;
  draftId: string;
  /** Defaults for the Person tab — pre-fills the organisation. */
  defaultOrganisation?: string | null;
  /** Called after a successful add (refresh the draft). */
  onAdded: () => void;
}

type Tab = 'person' | 'entity_channel';

const ROLE_OPTIONS: { value: PersonRoleEnum; label: string }[] = [
  { value: 'executive', label: 'Executive' },
  { value: 'board_member', label: 'Board member' },
  { value: 'investor_relations', label: 'Investor relations' },
  { value: 'media_contact', label: 'Media contact' },
  { value: 'pr_representative', label: 'PR representative' },
  { value: 'advisor', label: 'Advisor' },
  { value: 'analyst', label: 'Analyst' },
];

const CHANNEL_OPTIONS: { value: EntityChannelTypeEnum; label: string }[] = [
  { value: 'investor_relations', label: 'Investor relations (info@, ir@)' },
  { value: 'media', label: 'Media (media@, press@)' },
  { value: 'executive_office', label: 'Executive office (ceo@, chair@)' },
  { value: 'general', label: 'General enquiries' },
  { value: 'generic_inbox', label: 'Generic inbox' },
];

export function AddRecipientModal({
  isOpen,
  onClose,
  draftId,
  defaultOrganisation,
  onAdded,
}: AddRecipientModalProps) {
  const [tab, setTab] = useState<Tab>('person');

  // Person tab state
  const [pName, setPName] = useState('');
  const [pRole, setPRole] = useState<PersonRoleEnum>('executive');
  const [pOrg, setPOrg] = useState<string>(defaultOrganisation || '');
  const [pEmail, setPEmail] = useState('');
  const [pPhone, setPPhone] = useState('');

  // Entity channel tab state
  const [ecType, setEcType] = useState<EntityChannelTypeEnum>('investor_relations');
  const [ecEmail, setEcEmail] = useState('');
  const [ecPhone, setEcPhone] = useState('');
  const [ecLabel, setEcLabel] = useState('');

  // Shared submission state
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [candidate, setCandidate] = useState<FuzzyCandidate | null>(null);

  if (!isOpen) return null;

  const reset = () => {
    setPName('');
    setPRole('executive');
    setPOrg(defaultOrganisation || '');
    setPEmail('');
    setPPhone('');
    setEcType('investor_relations');
    setEcEmail('');
    setEcPhone('');
    setEcLabel('');
    setSubmitting(false);
    setError(null);
    setCandidate(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const submitPerson = async (overrides?: Partial<AddPersonRecipientRequest>) => {
    setSubmitting(true);
    setError(null);
    try {
      const body: AddPersonRecipientRequest = {
        kind: 'person',
        full_name: pName.trim(),
        role: pRole,
        organisation: pOrg.trim() || undefined,
        email: pEmail.trim(),
        phone: pPhone.trim() || undefined,
        ...overrides,
      };
      const result: AddRecipientResult = await addRecipient(draftId, body);
      if (result.status === 'candidate_review_required') {
        setCandidate(result.candidate);
        setSubmitting(false);
        return;
      }
      onAdded();
      handleClose();
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to add recipient.');
      setSubmitting(false);
    }
  };

  const submitEntityChannel = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const body: AddEntityChannelRequest = {
        kind: 'entity_channel',
        contact_type: ecType,
        channel_email: ecEmail.trim() || undefined,
        channel_phone: ecPhone.trim() || undefined,
        contact_name_raw: ecLabel.trim() || undefined,
      };
      if (!body.channel_email && !body.channel_phone) {
        throw new Error('Provide at least one of email or phone.');
      }
      await addRecipient(draftId, body);
      onAdded();
      handleClose();
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to add channel.');
      setSubmitting(false);
    }
  };

  return (
    <Modal onClose={handleClose} size="xl">
      <ModalHeader>
        <h2 className="text-lg font-semibold">Add recipient</h2>
        <button
          type="button"
          onClick={handleClose}
          className="text-grey-500 hover:text-grey-700 touch-target"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>
      </ModalHeader>

        {candidate ? (
          <ModalBody>
            <FuzzyCandidatePanel
              candidate={candidate}
              onUseExisting={() =>
                submitPerson({ cynapse_entity_id: candidate.entity_id || undefined })
              }
              onCreateNew={() => submitPerson({ force_new: true })}
              onCancel={() => {
                setCandidate(null);
                setSubmitting(false);
              }}
              submitting={submitting}
            />
          </ModalBody>
        ) : (
          <>
            <div className="flex-shrink-0 px-5 pt-3 flex gap-2 border-b border-grey-200">
              <TabButton active={tab === 'person'} onClick={() => setTab('person')}>
                <UserPlus className="w-4 h-4" /> Person
              </TabButton>
              <TabButton active={tab === 'entity_channel'} onClick={() => setTab('entity_channel')}>
                <AtSign className="w-4 h-4" /> Entity channel
              </TabButton>
            </div>

            <ModalBody>
            {tab === 'person' ? (
              <form
                className="space-y-3"
                onSubmit={(e) => {
                  e.preventDefault();
                  submitPerson();
                }}
              >
                <Field label="Full name">
                  <input
                    type="text"
                    value={pName}
                    onChange={(e) => setPName(e.target.value)}
                    required
                    autoFocus
                    className="w-full border border-grey-300 rounded px-2 py-1.5"
                  />
                </Field>
                <Field label="Role">
                  <select
                    value={pRole}
                    onChange={(e) => setPRole(e.target.value as PersonRoleEnum)}
                    className="w-full border border-grey-300 rounded px-2 py-1.5"
                  >
                    {ROLE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Organisation">
                  <input
                    type="text"
                    value={pOrg}
                    onChange={(e) => setPOrg(e.target.value)}
                    placeholder={defaultOrganisation || 'Optional'}
                    className="w-full border border-grey-300 rounded px-2 py-1.5"
                  />
                </Field>
                <Field label="Email">
                  <input
                    type="email"
                    value={pEmail}
                    onChange={(e) => setPEmail(e.target.value)}
                    required
                    className="w-full border border-grey-300 rounded px-2 py-1.5 font-mono text-sm"
                  />
                </Field>
                <Field label="Phone (optional)">
                  <input
                    type="tel"
                    value={pPhone}
                    onChange={(e) => setPPhone(e.target.value)}
                    className="w-full border border-grey-300 rounded px-2 py-1.5"
                  />
                </Field>
                <Footer
                  error={error}
                  submitting={submitting}
                  submitLabel="Add person"
                  onCancel={handleClose}
                />
                <p className="text-xs text-grey-500">
                  Person contacts go through Cynapse for deduplication. If a similar
                  contact already exists, you'll be asked whether to use the existing
                  one or create a new contact.
                </p>
              </form>
            ) : (
              <form
                className="space-y-3"
                onSubmit={(e) => {
                  e.preventDefault();
                  submitEntityChannel();
                }}
              >
                <Field label="Channel type">
                  <select
                    value={ecType}
                    onChange={(e) => setEcType(e.target.value as EntityChannelTypeEnum)}
                    className="w-full border border-grey-300 rounded px-2 py-1.5"
                  >
                    {CHANNEL_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Channel email">
                  <input
                    type="email"
                    value={ecEmail}
                    onChange={(e) => setEcEmail(e.target.value)}
                    placeholder="info@example.com.au"
                    className="w-full border border-grey-300 rounded px-2 py-1.5 font-mono text-sm"
                  />
                </Field>
                <Field label="Channel phone (optional)">
                  <input
                    type="tel"
                    value={ecPhone}
                    onChange={(e) => setEcPhone(e.target.value)}
                    className="w-full border border-grey-300 rounded px-2 py-1.5"
                  />
                </Field>
                <Field label="Display label (optional)">
                  <input
                    type="text"
                    value={ecLabel}
                    onChange={(e) => setEcLabel(e.target.value)}
                    placeholder="e.g. Investor Enquiries"
                    className="w-full border border-grey-300 rounded px-2 py-1.5"
                  />
                </Field>
                <Footer
                  error={error}
                  submitting={submitting}
                  submitLabel="Add channel"
                  onCancel={handleClose}
                />
                <p className="text-xs text-grey-500">
                  Entity channels (info@, media@, ir@) are stored as Assayer-side
                  metadata on the company entity. They surface as recipients on this
                  draft AND on future drafts for the same company.
                </p>
              </form>
            )}
            </ModalBody>
          </>
        )}
    </Modal>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        'inline-flex items-center gap-1.5 px-3 py-2 text-sm border-b-2 -mb-px transition-colors ' +
        (active
          ? 'border-primary-600 text-primary-700 font-medium'
          : 'border-transparent text-grey-600 hover:text-grey-800')
      }
    >
      {children}
    </button>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block text-sm">
      <span className="block text-grey-700 mb-1">{label}</span>
      {children}
    </label>
  );
}

function Footer({
  error,
  submitting,
  submitLabel,
  onCancel,
}: {
  error: string | null;
  submitting: boolean;
  submitLabel: string;
  onCancel: () => void;
}) {
  return (
    <>
      {error && (
        <div className="flex items-start gap-2 text-error-700 text-sm bg-error-50 px-2 py-1.5 rounded">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}
      <div className="flex justify-end gap-2 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-3 py-1.5 text-sm border border-grey-300 rounded hover:bg-grey-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={submitting}
          className="px-3 py-1.5 text-sm bg-primary-600 text-white rounded hover:bg-primary-700 disabled:bg-primary-300"
        >
          {submitting ? 'Adding…' : submitLabel}
        </button>
      </div>
    </>
  );
}

function FuzzyCandidatePanel({
  candidate,
  onUseExisting,
  onCreateNew,
  onCancel,
  submitting,
}: {
  candidate: FuzzyCandidate;
  onUseExisting: () => void;
  onCreateNew: () => void;
  onCancel: () => void;
  submitting: boolean;
}) {
  const sim = candidate.similarity_score ?? 0;
  return (
    <div className="space-y-3">
      <div className="flex items-start gap-2 text-amber-800 bg-amber-50 px-3 py-2 rounded">
        <CheckCircle2 className="w-5 h-5 mt-0.5 flex-shrink-0" />
        <div className="text-sm">
          <strong>Possible duplicate found.</strong> Cynapse already has a similar
          contact on file. You can route to the existing record (preferred when
          this person is the same individual under a different spelling) or
          create a separate new contact — Cynapse will queue the pair for a
          later merge decision via the MergeCandidatesPage.
        </div>
      </div>
      <div className="border border-grey-200 rounded px-3 py-2 bg-grey-50">
        <div className="text-sm text-grey-500">Existing Cynapse contact</div>
        <div className="text-base font-medium">{candidate.full_name || '—'}</div>
        <div className="text-xs text-grey-500 mt-1">
          Similarity: {(sim * 100).toFixed(0)}% • entity_id:{' '}
          <span className="font-mono">{candidate.entity_id?.slice(0, 8)}…</span>
        </div>
      </div>
      <div className="flex justify-end gap-2 pt-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={submitting}
          className="px-3 py-1.5 text-sm border border-grey-300 rounded hover:bg-grey-50"
        >
          Back
        </button>
        <button
          type="button"
          onClick={onCreateNew}
          disabled={submitting}
          className="px-3 py-1.5 text-sm border border-grey-300 rounded hover:bg-grey-50"
        >
          Create new anyway
        </button>
        <button
          type="button"
          onClick={onUseExisting}
          disabled={submitting}
          className="px-3 py-1.5 text-sm bg-primary-600 text-white rounded hover:bg-primary-700 disabled:bg-primary-300"
        >
          {submitting ? 'Adding…' : 'Use existing'}
        </button>
      </div>
    </div>
  );
}
